/* Quality over time — the estate's, or one dataset's once a row is chosen.
 *
 * Three properties make this a chart rather than decoration, and each is a fact about this
 * platform's data rather than a styling choice:
 *
 * **A line is never drawn across a score-model change.** `Trend.segments` is the rendering
 * primitive: joining across a boundary presents a reweighting as a regression, and six points of
 * apparent decline may be a reweighting and not a decline at all. The break is drawn, marked, and
 * named in the legend.
 *
 * **Coverage is a second line, on the same axis.** Both are percentages of a whole, so this is one
 * scale and not a dual axis. It is the most valuable second series this product has: a rising score
 * over falling coverage is a score improving because it is checking less, which is the most
 * misleading shape a quality chart can have and is invisible without it.
 *
 * **A gap is drawn as a gap.** A day with nothing scored is absent from the estate series and null
 * in a dataset's; both break the path. Closing over them would draw a straight line through a
 * period nobody measured.
 *
 * Per-dimension history is real and is **opt-in**, because it is one request per point: a
 * `TrendPoint` carries a `score_id` and the dimensions live on the `Score` behind it. The control
 * says how many requests it will make before it makes them.
 */

import { useMemo, useState } from 'react';
import type { PointerEvent as ReactPointerEvent, ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { SkeletonRows } from '@/shared/ui/Skeleton';
import { UnavailableState } from '@/shared/ui/States';

import { useDatasetTrend, useEstateTrend } from '../api/scoring';
import type { Dimension, Score, TrendPoint } from '../api/scoring';
import { HISTORY_READ_LIMIT, useScoresByIds } from '../api/scoresBatch';
import { DIMENSION_ORDER } from '../model/dimensions';
import { formatScore } from '../model/presentation';

/** The three validated categorical slots this design system publishes, named rather than valued.
 *
 * **A tone, not a colour string.** Every line, endpoint, legend key and tooltip swatch takes its
 * paint from a class, so the hue lives in the stylesheet where the dark-theme swap already is — and
 * a component cannot hold a `var(--series-1)` that the theme layer does not know it is using.
 *
 * Three, and a fourth dimension is refused rather than given a generated hue: a categorical palette
 * that invents a colour is one whose fourth and fifth series are indistinguishable to a
 * colour-blind reader, and these three are validated against each other and against both surfaces.
 */
const SERIES_TONES = ['s1', 's2', 's3'] as const;
const MAX_DIMENSION_LINES = SERIES_TONES.length;

type LineTone = 'score' | 'coverage' | (typeof SERIES_TONES)[number];

const VIEW_W = 1000;
const VIEW_H = 210;
const PAD_L = 38;
const PAD_R = 62;
const PAD_T = 14;
const PAD_B = 26;
const PLOT_W = VIEW_W - PAD_L - PAD_R;
const PLOT_H = VIEW_H - PAD_T - PAD_B;

interface Datum {
  readonly x: number;
  readonly label: string;
  readonly value: number | null;
  readonly coverage: number | null;
  readonly model: number | null;
  readonly scoreId: string | null;
  /** Datasets behind the estate mean that day; null in dataset mode. */
  readonly scored: number | null;
  readonly evidenceComplete: boolean;
}

interface Line {
  readonly key: string;
  readonly label: string;
  /** Which class paints it. The stylesheet owns the hue, the dash and the weight. */
  readonly tone: LineTone;
  readonly values: readonly (number | null)[];
}

function dayLabel(iso: string): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return iso;
  return at.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
}

/** Paths for one series, broken at every null and at every model change.
 *
 * `breaks` holds the indices a new path must start at. Passing them in rather than deriving them
 * per series is what keeps the score line, the coverage line and every dimension line breaking at
 * exactly the same points — three series that disagreed about where a model changed would draw a
 * boundary that exists on one line and not the next.
 */
function paths(
  values: readonly (number | null)[],
  breaks: ReadonlySet<number>,
  x: (index: number) => number,
  y: (value: number) => number,
): readonly string[] {
  const out: string[] = [];
  let current: string[] = [];
  values.forEach((value, index) => {
    if (value === null) {
      if (current.length > 1) out.push(current.join(' '));
      current = [];
      return;
    }
    if (breaks.has(index) && current.length > 0) {
      if (current.length > 1) out.push(current.join(' '));
      current = [];
    }
    current.push(
      `${current.length === 0 ? 'M' : 'L'}${x(index).toFixed(1)} ${y(value).toFixed(1)}`,
    );
  });
  if (current.length > 1) out.push(current.join(' '));
  return out;
}

export interface QualityTrendProps {
  readonly days: number;
  readonly onDaysChange: (days: number) => void;
  readonly periods: readonly number[];
  /** `null` shows the estate. A chosen row shows that dataset, with its coverage beside its score. */
  readonly datasetId: string | null;
  readonly datasetName: string | null;
  readonly onClearDataset: () => void;
}

export function QualityTrend({
  days,
  onDaysChange,
  periods,
  datasetId,
  datasetName,
  onClearDataset,
}: QualityTrendProps): ReactElement {
  const { t } = useTranslation();
  const [showCoverage, setShowCoverage] = useState(true);
  const [dimensions, setDimensions] = useState<readonly Dimension[]>([]);
  const [historyWanted, setHistoryWanted] = useState(false);
  const [hover, setHover] = useState<number | null>(null);

  const estate = useEstateTrend(days);
  const dataset = useDatasetTrend(datasetId ?? '', days);
  const isDataset = datasetId !== null;

  const trendPoints: readonly TrendPoint[] = useMemo(
    () => (isDataset ? (dataset.data?.points ?? []) : []),
    [isDataset, dataset.data],
  );

  /* Bounded to the newest `HISTORY_READ_LIMIT` points. A ninety-day window on a nightly schedule is
     ninety scores, and ninety requests to draw three lines is the fan-out this file refuses. */
  const historyIds = useMemo(
    () => trendPoints.slice(-HISTORY_READ_LIMIT).map((point) => point.score_id),
    [trendPoints],
  );
  const history = useScoresByIds(
    historyIds,
    isDataset && historyWanted && dimensions.length > 0,
  );

  const data: readonly Datum[] = useMemo(() => {
    if (isDataset) {
      return trendPoints.map((point, index) => ({
        x: index,
        label: dayLabel(point.computed_at),
        value: point.value,
        coverage: point.column_coverage === null ? null : point.column_coverage * 100,
        model: point.score_model_version,
        scoreId: point.score_id,
        scored: null,
        evidenceComplete: point.evidence_complete,
      }));
    }
    return (estate.data?.items ?? []).map((point, index) => ({
      x: index,
      label: dayLabel(point.day),
      value: point.value,
      coverage: null,
      model: null,
      scoreId: null,
      scored: point.scored,
      evidenceComplete: true,
    }));
  }, [isDataset, trendPoints, estate.data]);

  /* Where a new path must start: a model change. Derived from the points rather than from
     `segments`, which is the same split — `segments` is defined as one inner array per contiguous
     run under a single model version — and needs no index mapping back into the series. */
  const breaks = useMemo(() => {
    const found = new Set<number>();
    data.forEach((point, index) => {
      if (index === 0) return;
      const previous = data[index - 1];
      if (previous === undefined || point.model === null || previous.model === null)
        return;
      if (point.model !== previous.model) found.add(index);
    });
    return found;
  }, [data]);

  const dimensionLines: readonly Line[] = useMemo(() => {
    if (!isDataset || history.byKey.size === 0) return [];
    return dimensions.slice(0, MAX_DIMENSION_LINES).map((dimension, slot) => ({
      key: dimension,
      label: t(`scoring.dimension.${dimension}`),
      tone: SERIES_TONES[slot % MAX_DIMENSION_LINES] ?? SERIES_TONES[0],
      values: data.map((point) => {
        if (point.scoreId === null) return null;
        const score: Score | undefined = history.byKey.get(point.scoreId);
        if (score === undefined) return null;
        return (
          score.dimensions.find((entry) => entry.dimension === dimension)?.value ?? null
        );
      }),
    }));
  }, [isDataset, history.byKey, dimensions, data, t]);

  const lines: readonly Line[] = useMemo(() => {
    const base: Line[] = [
      {
        key: 'score',
        label: isDataset
          ? t('scoring.trend.datasetScore')
          : t('scoring.trend.estateScore'),
        /* Ink rather than a hue: the score is not a peer of the dimensions beside it, and saying so
           with weight keeps the categorical set at three validated slots. */
        tone: 'score',
        values: data.map((point) => point.value),
      },
    ];
    if (isDataset && showCoverage) {
      base.push({
        key: 'coverage',
        label: t('scoring.trend.coverage'),
        /* Achromatic and dashed. Coverage is a different measure sharing one scale, so it is
           deliberately not given a categorical hue — the dash is what separates it. */
        tone: 'coverage',
        values: data.map((point) => point.coverage),
      });
    }
    return [...base, ...dimensionLines];
  }, [isDataset, showCoverage, data, dimensionLines, t]);

  const measured = lines
    .flatMap((line) => line.values)
    .filter((v): v is number => v !== null);
  const low =
    measured.length === 0
      ? 0
      : Math.max(0, Math.floor((Math.min(...measured) - 4) / 5) * 5);
  const highRaw =
    measured.length === 0 ? 100 : Math.ceil((Math.max(...measured) + 4) / 5) * 5;
  const high = Math.min(100, Math.max(highRaw, low + 10));

  const x = (index: number): number =>
    data.length <= 1 ? PAD_L + PLOT_W / 2 : PAD_L + (index / (data.length - 1)) * PLOT_W;
  const y = (value: number): number =>
    PAD_T + PLOT_H - ((value - low) / (high - low)) * PLOT_H;

  const ticks = [0, 1, 2, 3, 4].map((step) => low + ((high - low) / 4) * step);

  const pending = isDataset ? dataset.isPending : estate.isPending;
  const failed = isDataset ? dataset.isError : estate.isError;
  const errorMessage = isDataset ? dataset.error?.message : estate.error?.message;

  const toggleDimension = (dimension: Dimension): void => {
    setDimensions((current) => {
      if (current.includes(dimension))
        return current.filter((entry) => entry !== dimension);
      if (current.length >= MAX_DIMENSION_LINES) return current;
      return [...current, dimension];
    });
    setHistoryWanted(true);
  };

  const hovered = hover === null ? undefined : data[hover];

  const onMove = (event: ReactPointerEvent<HTMLDivElement>): void => {
    if (data.length === 0) return;
    const box = event.currentTarget.getBoundingClientRect();
    if (box.width === 0) return;
    const asViewBox = ((event.clientX - box.left) / box.width) * VIEW_W;
    const fraction = (asViewBox - PAD_L) / PLOT_W;
    const index = Math.round(fraction * (data.length - 1));
    setHover(Math.max(0, Math.min(data.length - 1, index)));
  };

  return (
    <section className="lq-qtrend" aria-label={t('scoring.trend.region')}>
      <div className="lq-qtrend__head">
        <h2 className="lq-qtrend__title">
          {isDataset ? t('scoring.trend.titleDataset') : t('scoring.trend.titleEstate')}
        </h2>
        <span className="lq-qtrend__subject lq-mono">{isDataset ? datasetName : ''}</span>
        {isDataset && (
          <button type="button" className="lq-linkish" onClick={onClearDataset}>
            {t('scoring.trend.backToEstate')}
          </button>
        )}
        <span className="lq-qtrend__spacer" />
        <div
          className="lq-qtrend__periods"
          role="group"
          aria-label={t('scoring.trend.period')}
        >
          {periods.map((option) => (
            <button
              key={option}
              type="button"
              className="lq-qtrend__period"
              aria-pressed={option === days}
              onClick={() => {
                onDaysChange(option);
              }}
            >
              {t('scoring.trend.days', { days: option })}
            </button>
          ))}
        </div>
      </div>

      {pending ? (
        <div className="lq-qtrend__plot">
          <SkeletonRows rows={3} columns={6} />
        </div>
      ) : failed ? (
        <div className="lq-qtrend__plot">
          <UnavailableState
            title={t('scoring.trend.unreadable')}
            guidance={errorMessage ?? t('scoring.trend.unreadableGuidance')}
          />
        </div>
      ) : data.length < 2 ? (
        <div className="lq-qtrend__plot">
          <p className="lq-muted lq-qtrend__thin">
            {t('scoring.trend.tooFewPoints', { count: data.length })}
          </p>
        </div>
      ) : (
        <div
          className="lq-qtrend__plot"
          onPointerMove={onMove}
          onPointerLeave={() => {
            setHover(null);
          }}
        >
          <svg
            className="lq-qtrend__svg"
            viewBox={`0 0 ${String(VIEW_W)} ${String(VIEW_H)}`}
            preserveAspectRatio="none"
            role="img"
            aria-label={t('scoring.trend.figureLabel', {
              subject: isDataset ? (datasetName ?? '') : t('scoring.trend.estateScore'),
              days,
            })}
          >
            {ticks.map((tick) => (
              <g key={tick}>
                <line
                  x1={PAD_L}
                  x2={VIEW_W - PAD_R}
                  y1={y(tick)}
                  y2={y(tick)}
                  stroke="var(--border-subtle)"
                  strokeWidth={1}
                  fill="none"
                />
                <text
                  x={PAD_L - 8}
                  y={y(tick) + 3.5}
                  textAnchor="end"
                  fontSize={10}
                  fill="var(--text-muted)"
                  fontFamily="var(--lumiq-font-mono)"
                >
                  {tick.toFixed(0)}
                </text>
              </g>
            ))}

            {[...breaks].map((index) => (
              <line
                key={`break-${String(index)}`}
                x1={x(index)}
                x2={x(index)}
                y1={PAD_T}
                y2={PAD_T + PLOT_H}
                stroke="var(--status-warn)"
                strokeWidth={1}
                strokeDasharray="3 3"
                fill="none"
              />
            ))}

            {hovered !== undefined && (
              <line
                x1={x(hovered.x)}
                x2={x(hovered.x)}
                y1={PAD_T}
                y2={PAD_T + PLOT_H}
                stroke="var(--text-primary)"
                strokeWidth={1}
                opacity={0.28}
                fill="none"
              />
            )}

            {/* Reversed so the emphasised score line is painted last and sits above the rest. */}
            {[...lines].reverse().map((line) => {
              /* The last *measured* point, which is where the endpoint dot and the direct label
                 go. Found as an index first so a trailing gap does not put the label on the wrong
                 x position — the last value and the last non-null value are different points on a
                 series with a gap at the end, and that is exactly the series this chart draws. */
              let lastIndex = -1;
              line.values.forEach((value, index) => {
                if (value !== null) lastIndex = index;
              });
              const last = lastIndex < 0 ? null : (line.values[lastIndex] ?? null);
              return (
                <g key={line.key}>
                  {paths(line.values, breaks, x, y).map((d, index) => (
                    <path
                      key={`${line.key}-${String(index)}`}
                      className={`lq-qtrend__line lq-qtrend__line--${line.tone}`}
                      d={d}
                    />
                  ))}
                  {last !== null && lastIndex >= 0 && (
                    <>
                      <circle
                        className={`lq-qtrend__dot lq-qtrend__dot--${line.tone}`}
                        cx={x(lastIndex)}
                        cy={y(last)}
                        r={3.4}
                      />
                      {/* Direct endpoint labels: identity without relying on colour, and the relief
                          a series whose hue sits under 3:1 against the surface obliges. */}
                      <text
                        x={x(lastIndex) + 9}
                        y={y(last) + 4}
                        fontSize={11}
                        fontWeight={600}
                        fill="var(--text-primary)"
                        fontFamily="var(--lumiq-font-mono)"
                      >
                        {formatScore(last)}
                      </text>
                    </>
                  )}
                </g>
              );
            })}

            {[0, Math.floor((data.length - 1) / 2), data.length - 1].map(
              (index, slot) => (
                <text
                  key={`x-${String(index)}`}
                  x={x(index)}
                  y={VIEW_H - 7}
                  textAnchor={slot === 0 ? 'start' : slot === 2 ? 'end' : 'middle'}
                  fontSize={10}
                  fill="var(--text-muted)"
                  fontFamily="var(--lumiq-font-mono)"
                >
                  {data[index]?.label ?? ''}
                </text>
              ),
            )}
          </svg>

          {hovered !== undefined && (
            <div
              className="lq-qtrend__tip"
              style={{
                left: `${String(Math.min(72, (x(hovered.x) / VIEW_W) * 100))}%`,
              }}
              role="status"
            >
              <p className="lq-qtrend__tipday">{hovered.label}</p>
              {lines.map((line) => {
                const value = line.values[hovered.x] ?? null;
                const previous =
                  hovered.x > 0 ? (line.values[hovered.x - 1] ?? null) : null;
                const change =
                  value !== null && previous !== null ? value - previous : null;
                return (
                  <p key={line.key} className="lq-qtrend__tiprow">
                    <span>
                      <span
                        className={`lq-qtrend__swatch lq-qtrend__swatch--${line.tone}`}
                        aria-hidden="true"
                      />
                      {line.label}
                    </span>
                    <span className="lq-mono">
                      {value === null
                        ? t('scoring.trend.notMeasured')
                        : formatScore(value)}
                      {change !== null && Math.abs(change) >= 0.1 && (
                        <span
                          className={
                            change > 0
                              ? 'lq-delta lq-delta--up'
                              : 'lq-delta lq-delta--down'
                          }
                        >
                          {' '}
                          {change > 0 ? '+' : '−'}
                          {formatScore(Math.abs(change))}
                        </span>
                      )}
                    </span>
                  </p>
                );
              })}
              <p className="lq-qtrend__tipnote">
                {isDataset
                  ? t('scoring.trend.tipRun', {
                      model: hovered.model ?? 0,
                      evidence: hovered.evidenceComplete
                        ? t('scoring.trend.evidenceComplete')
                        : t('scoring.trend.evidencePartial'),
                    })
                  : t('scoring.trend.tipEstate', { count: hovered.scored ?? 0 })}
              </p>
            </div>
          )}
        </div>
      )}

      <div className="lq-qtrend__legend">
        <span className="lq-qtrend__key">
          <span className="lq-qtrend__swatch lq-qtrend__swatch--ink" aria-hidden="true" />
          {isDataset ? t('scoring.trend.datasetScore') : t('scoring.trend.estateScore')}
        </span>

        {isDataset && (
          <button
            type="button"
            className="lq-chip"
            aria-pressed={showCoverage}
            onClick={() => {
              setShowCoverage((on) => !on);
            }}
            title={t('scoring.trend.coverageHint')}
          >
            <span
              className="lq-chip__swatch lq-chip__swatch--dashed"
              aria-hidden="true"
            />
            {t('scoring.trend.coverage')}
          </button>
        )}

        {isDataset &&
          DIMENSION_ORDER.map((dimension) => {
            const slot = dimensions.indexOf(dimension);
            const on = slot >= 0;
            const full = !on && dimensions.length >= MAX_DIMENSION_LINES;
            const tone = SERIES_TONES[slot % MAX_DIMENSION_LINES] ?? SERIES_TONES[0];
            return (
              <button
                key={dimension}
                type="button"
                className="lq-chip"
                aria-pressed={on}
                disabled={full}
                title={
                  full
                    ? t('scoring.trend.dimensionsFull')
                    : t('scoring.trend.dimensionHint')
                }
                onClick={() => {
                  toggleDimension(dimension);
                }}
              >
                {/* The toggle is also the legend: the swatch takes the slot's colour the moment
                    the line is on, so the mapping from hue to dimension lives in one place. */}
                <span
                  className={
                    on ? `lq-chip__swatch lq-chip__swatch--${tone}` : 'lq-chip__swatch'
                  }
                  aria-hidden="true"
                />
                {t(`scoring.dimension.${dimension}`)}
              </button>
            );
          })}

        <span className="lq-qtrend__spacer" />

        {breaks.size > 0 && (
          <span className="lq-qtrend__note lq-qtrend__note--warn">
            {t('scoring.trend.modelBreak', { count: breaks.size })}
          </span>
        )}
        {isDataset && dimensions.length > 0 && (
          <span className="lq-qtrend__note">
            {history.loading
              ? t('scoring.trend.historyLoading', { count: history.requested })
              : history.failed
                ? t('scoring.trend.historyFailed')
                : t('scoring.trend.historyRead', {
                    read: history.resolved,
                    total: trendPoints.length,
                  })}
          </span>
        )}
        {!isDataset && (
          <span className="lq-qtrend__note">{t('scoring.trend.pickHint')}</span>
        )}
      </div>
    </section>
  );
}
